/**
 * @file add-purchase-order.tsx
 * @deprecated Copied to app/modules/procurement/pages/po-management/add-purchase-order.tsx. This file still serves the existing /UProcure routes.
 * @module app/modules/uProcure/po-management/add-purchase-order
 * @description Form component for creating new purchase orders. Allows selection of vendors,
 *   products with quantity types (Piece, Dozen, Carton), delivery dates, and transport modes.
 *   Includes product search with debouncing, dynamic product row management with add/remove
 *   functionality, and form validation before submission to backend API.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 *
 * @todo FEATURE: Add debounce functionality to product search input
 *   - Implement handleProductDebounce for product search (500ms delay)
 *   - Current: handleProductInputChange calls API directly on every keystroke
 *   - Should call debounced version instead
 *   - Reference: uprocure-admin lines 240-243
 *
 * @todo FEATURE: Populate forecastedDaysData from fetchStockLeft API response
 *   - fetchStockLeft currently fetches but doesn't store forecastedDaysData
 *   - Need to populate products[index].forecastedDaysData with API response
 *   - Then display warnings in quantity section for low-stock predictions
 *   - Reference: uprocure-admin lines 408-432
 *
 * @todo ENHANCEMENT: Implement per-index product pagination
 *   - Current: fetchProduct is basic, doesn't support per-product-row pagination
 *   - Uprocure: fetches products per product row index (sellerProducts[index])
 *   - Allows different products to have different pagination states
 *   - May not be critical but improves UX for large catalogs
 *   - Reference: uprocure-admin lines 296-330, 363-375
 *
 * @todo ENHANCEMENT: Implement complex quantityTypes deduplication in handleSubmit
 *   - Current: filters quantity types with stockCount > 0 && purchaseAmount > 0
 *   - Uprocure: uses uniqueByTypeWarehouse Map to deduplicate by type+warehouse combo
 *   - May be important for multi-warehouse scenarios to prevent duplicate submissions
 *   - Reference: uprocure-admin lines 1017-1030
 *
 * @todo REFACTOR: Add Method utility for date conversion
 *   - Currently using manual ISO conversion: new Date().setHours().toISOString()
 *   - Should use Method.convertDateToDDMMYYYY() if available
 *   - Verify if Method utility has compatible function
 *   - Reference: uprocure-admin line 1051
 *
 * @todo CLEANUP: Remove unused state variables
 *   - [page] - unused (line 122)
 *   - [searchTerm, setSearchTerm] - unused (line 124) - keep for future product search
 *   - [totalRecords, setTotalRecords] - unused (line 125) - keep for future pagination
 *   - [costDetails, setCostDetails] - unused (line 559) - keep for future cost calculations
 *   - Can add eslint-disable comments if these are intentionally reserved for future use
 */

import { Button, Card, Col, Form, InputGroup, Row } from "react-bootstrap";
import { CustomSelect } from "../../../custom/Select/CustomSelect";
import GrayCross from "../../../../_admin/assets/media/svg_uTrade/cross-red.svg";
import { useNavigate } from "react-router-dom";
import { error, success } from "../../../../Global/toast";
import Method from "../../../../utils/methods";
import { useAuth } from "../../auth";
import { DRAFT_PO_TAB } from "./draft-po-list";
import { Fragment, useEffect, useState } from "react";
import clsx from "clsx";
import Loader from "../../../../Global/loader";
import { useDebounce } from "../../../../utils/useDebounce";
import { setKey, removeKey } from "../../../../Global/history";
import { listPurchaseOrder } from "../../../../utils/storeString";
import {
  AllSeller,
  CartonWithDozens,
  CartonWithPieces,
  Dozen,
  PAGE_LIMIT,
  Piece,
  SelfTransport,
  UTradeTransport,
  WarehouseManger,
  Admin,
  PermissionModule,
  Permissions,
} from "../../../../utils/constants";
import CustomDatePicker from "../../../custom/DateRange/CustomDatePicker";
import {
  manageProduct,
  PURCHASEORDER,
  vendor,
} from "../../../../api/apiEndPoints";
import APICallService from "../../../../api/apiCallService";
import { PriceCatalog } from "../../../../types";
import { useWarehouse } from "../../../context/WarehouseContext";
import { getQuantityTypeLabel } from "../../../../utils";

/** A qty type row of the PO form. */
interface PoFormQtyType {
  type: number | string;
  stockCount?: number | string;
}
/** A qty type of the last completed PO (product-price API). */
interface LastPoApiType {
  type: number | string;
  stockCount?: number;
  refKey?: string | null;
  lastShipmentDate?: string | null;
}
/** A line saved to a draft PO. */
interface DraftLineInput {
  variant: string;
  quantityType: number;
  quantity: number;
}

interface LastPoInfo {
  stockCount: number;
  refKey: string;
  date: string | null;
}

/** "Last PO: 600 (S1234, 12/09/2026)" under a qty type, from the last completed PO. */
const LastPoQty = ({ info }: { info?: LastPoInfo }) =>
  info ? (
    <div className="fs-12 fw-600 text-gray-700 mt-2" title="Quantity on the last completed PO of this product and qty type">
      Last PO: {info.stockCount.toLocaleString("en-US")}
      {info.refKey ? ` (${info.refKey}` : " ("}
      {info.date ? `${info.refKey ? ", " : ""}${Method.convertDateToFormat(info.date, "DD/MM/YYYY")})` : ")"}
    </div>
  ) : null;

const AddPurchaseOrder = () => {
  const navigate = useNavigate();
  const [totalVendors, setTotalVendors] = useState(0);
  const [vendorPage, setVendorPage] = useState(1);
  const [sellerData, setSellerData] = useState<any[]>([]);
  const [selectedSeller, setSelectedSeller] = useState("");
  const [selectedSellerData, setSelectedSellerData] = useState<any>(null);
  const [sellerProducts, setSellerProducts] = useState<any>([]);
  const [priceLoading, setPriceLoading] = useState<boolean[]>([false]);
  /** last completed PO per product and qty type in this warehouse: variant -> type -> info */
  const [lastPo, setLastPo] = useState<Record<string, Record<number, LastPoInfo>>>({});
  const [loading, setLoading] = useState(false);
  const [fetchLoader, setFetchLoader] = useState(false);
  const [productFetchLoader, setProductsFetchLoader] = useState(false);
  const [vendorSearchTerm, setVendorSearchTerm] = useState("");
  const [umartData, setUmartData] = useState<any>(true);
  const [deliveryDate, setDeliveryDate] = useState<Date | null>(null);
  const [productPage, setProductPage] = useState(1);
  const [, setSearchTerm] = useState("");
  const [totalRecords] = useState(0);
  const [shipmentType, setShipmentType] = useState(UTradeTransport);
  const [products, setProducts] = useState<
    {
      variant: string;
      quantityTypes: any;
      selectedOption?: any;
      forecastedDaysData?: any;
    }[]
  >([
    {
      variant: "",
      quantityTypes: [],
      selectedOption: null,
      forecastedDaysData: null,
    },
  ]);
  const [productValidation, setProductValidation] = useState<
    {
      variant: boolean;
      quantityTypes: boolean;
    }[]
  >([
    {
      variant: false,
      quantityTypes: false,
    },
  ]);

  const { selectedWarehouse, setSelectedWarehouse, warehouseList } =
    useWarehouse();

  const fetchVendorsList = async (page: number, search: string) => {
    try {
      setLoading(true);

      let params: any = {
        "status[]": 4,
        pageNo: page || 1,
        limit: PAGE_LIMIT,
        searchTerm: search || "",
        needCount: true,
      };

      const apiService = new APICallService(
        vendor.listVendor,
        params,
        "",
        "",
        "",
        "",
        AllSeller,
      );

      const response = await apiService.callAPI();

      if (response) {
        setTotalVendors(response.total || 0);
        if (page === 1) {
          setSellerData(response?.records || []);
        } else {
          setSellerData((prev) => [...prev, ...(response?.records || [])]);
        }
      }
    } catch (error) {
      console.error("Vendor list API failed", error);
    } finally {
      setLoading(false);
    }
  };

  const getUMartWarehouseList = () => {
    setLoading(true);
    setLoading(false);
  };

  useEffect(() => {
    (async () => {
      setFetchLoader(true);
      await Promise.all([fetchVendorsList(1, ""), getUMartWarehouseList()]);
      setFetchLoader(false);
    })();
  }, []);

  const handleDateChange = (date: Date | null) => {
    setDeliveryDate(date);
  };

  const onMenuScrollToBottom = async () => {
    if (sellerData.length < totalVendors) {
      const nextPage = vendorPage + 1;
      setVendorPage(nextPage);
      await fetchVendorsList(nextPage, vendorSearchTerm);
    }
  };

  const onMenuClose = async () => {
    if (vendorSearchTerm) {
      setVendorSearchTerm("");
      setVendorPage(1);
      await fetchVendorsList(1, "");
    }
  };

  const onMenuOpen = async () => {
    // optional reset if needed
  };

  const handleVendorDebounce = useDebounce(fetchVendorsList, 500);

  const handleInputChange = async (inputValue: string) => {
    setVendorPage(1);
    setVendorSearchTerm(inputValue);
    await handleVendorDebounce(1, inputValue);
  };

  const isDateDisabled = (date: Date) => {
    if (!selectedSellerData || !selectedSellerData.workingDays) return true;
    const workingDays = selectedSellerData.workingDays.map(Number);
    return workingDays.includes(date.getDay());
  };

  const handleSeller = async (event: any) => {
    setDeliveryDate(null);
    try {
      setProductsFetchLoader(true);
      if (event.value !== selectedSeller) {
        setProducts([
          {
            variant: "",
            quantityTypes: [],
            selectedOption: null,
            forecastedDaysData: null,
          },
        ]);
        setProductValidation([
          {
            variant: false,
            quantityTypes: false,
          },
        ]);
        setSellerProducts([]);
        let params: any = {
          "business[]": event.value,
        };
        const apiService = new APICallService(
          PURCHASEORDER.productListForPurchaseOrder,
          params,
          "",
          "",
          "",
          "",
          WarehouseManger,
          "",
        );
        const response = await apiService.callAPI();
        if (response) {
          setSellerProducts(response.records || []);
        }
      }
      setSelectedSellerData(event);
      setSelectedSeller(event.value);
    } catch (error) {
      console.error("Vendor list API failed", error);
    } finally {
      setProductsFetchLoader(false);
    }
  };
  const handleProductSelect = async (event: any, index: number) => {
    if (!event?.value) {
      return;
    }
    let temp = [...products];
    let productTemp = [...sellerProducts];
    const filteredArray: any = productTemp.filter(
      (obj: any) => obj._id === event.value,
    );
    if (!filteredArray.length) {
      return;
    }

    const selectedQuantityTypes = filteredArray[0].quantityTypes || [];
    const selectedOption = {
      label: (
        <>
          <div className="symbol symbol-30px border me-3">
            <img
              src={filteredArray[0]?.media?.[0]?.url}
              className="object-fit-contain"
              alt=""
            />
          </div>
          <span className="fs-16 fw-600 text-black mb-0">
            {filteredArray[0].title.replace(/\s*\)\s*/g, ")")}
          </span>
        </>
      ),
      value: filteredArray[0]._id,
      title: filteredArray[0].title,
    };

    temp[index].quantityTypes = selectedQuantityTypes;
    temp[index].variant = event.value;
    temp[index].selectedOption = selectedOption;
    setProducts(temp);

    await Promise.all([
      fetchStockLeft(event.value),
      fetchPriceCatalogPrice(selectedWarehouse?._id, event.value, index),
      fetchLastPo(event.value),
    ]);
  };

  const fetchLastPo = async (variantId: string) => {
    if (!variantId || !selectedWarehouse?._id) return;
    try {
      const api = new APICallService(
        PURCHASEORDER.productPrice,
        { warehouse: selectedWarehouse._id },
        { variant: variantId },
      );
      const res = await api.callAPI();
      const byType: Record<number, LastPoInfo> = {};
      const types: LastPoApiType[] = res?.record?.quantityTypes || [];
      types.forEach((q) => {
        byType[Number(q.type)] = {
          stockCount: Number(q.stockCount) || 0,
          refKey: q.refKey || "",
          date: q.lastShipmentDate || null,
        };
      });
      setLastPo((prev) => ({ ...prev, [variantId]: byType }));
    } catch (e) {
      console.error("Last PO fetch failed", e);
    }
  };

  const fetchPriceCatalogPrice = async (
    warehouseId: string | undefined,
    variantId: string,
    index: number,
  ) => {
    if (!warehouseId) {
      return;
    }
    try {
      setPriceLoading((prev) => {
        const updated = [...prev];
        updated[index] = true;
        return updated;
      });

      const apiService = new APICallService(
        manageProduct.getPriceCatalogEntry,
        `${variantId}?project=${encodeURIComponent(
          JSON.stringify({ pricing: 1 }),
        )}&warehouse=${warehouseId}`,
        "",
        "",
        "",
        "",
      );
      const response: PriceCatalog = await apiService.callAPI();
      const payload = response?.pricing || [];

      // Cost price always comes from the active pricing entry for the warehouse
      const activeQuantityTypes =
        payload.find((p) => p.active)?.quantityTypes || [];

      setProducts((prevProducts: any) => {
        const updatedProducts = [...prevProducts];
        if (!updatedProducts[index]) {
          return prevProducts;
        }

        const currentProduct = { ...updatedProducts[index] };
        currentProduct.quantityTypes = (currentProduct.quantityTypes || []).map(
          (qt: any) => {
            const matched = activeQuantityTypes.find(
              (aqt: any) => aqt.type === qt.type,
            );
            return {
              ...qt,
              purchaseAmount: matched?.costPrice || 0,
            };
          },
        );
        updatedProducts[index] = currentProduct;
        return updatedProducts;
      });
    } catch (error) {
      console.error("Failed to fetch price catalog data", error);
    } finally {
      setPriceLoading((prev) => {
        const updated = [...prev];
        updated[index] = false;
        return updated;
      });
    }
  };

  const fetchStockLeft = async (variantId: string) => {
    try {
      const apiService = new APICallService(
        PURCHASEORDER.lowStock,
        { warehouse: selectedWarehouse?._id },
        { variant: variantId },
        "",
        "",
        "",
        WarehouseManger,
        selectedWarehouse?._id || "",
      );
      await apiService.callAPI();
    } catch (error) {
      console.error("Low stock API failed", error);
    }
  };

  const onMenuProductScrollToBottom = async () => {
    if (!(sellerProducts.length === totalRecords)) {
      let tempPage = productPage;
      tempPage = tempPage + 1;
      setProductPage(tempPage);
    }
  };
  const onMenuProductClose = async () => {
    setSearchTerm("");
  };
  const onMenuProductOpen = async () => {
    setProductPage(1);
  };
  const handleProductInputChange = async (inputValue: string) => {
    setProductPage(1);
    setSearchTerm(inputValue);
    // TODO: Add debounced search (useDebounce) to avoid excessive API calls on every keystroke
  };

  // Re-fetch prices when warehouse changes
  useEffect(() => {
    if (!selectedWarehouse?._id) return;
    products.forEach((product, index) => {
      if (product?.variant) {
        fetchPriceCatalogPrice(selectedWarehouse._id, product.variant, index);
        fetchLastPo(product.variant);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWarehouse]);

  const handleQuantityChange = (
    value: string,
    index: number,
    quantityIndex: number,
    type: number,
    field: "stockCount" | "purchaseAmount" = "stockCount",
  ) => {
    let productTemp = [...products];
    let productValidationTemp = [...productValidation];
    if (field === "purchaseAmount") {
      productValidationTemp[index].variant = true;
      if (parseInt(value) > 0) {
        productValidationTemp[index].variant = false;
      }
    } else {
      productValidationTemp[index].quantityTypes = true;
      if (parseInt(value) > 0) {
        productValidationTemp[index].quantityTypes = false;
      }
    }

    const qt = productTemp[index].quantityTypes[quantityIndex];
    if (!qt) return;
    if (field === "stockCount") {
      qt["type"] = type;
      qt["stockCount"] = Number(value);
    } else if (field === "purchaseAmount") {
      qt["purchaseAmount"] = Number(value);
    }

    productTemp[index].quantityTypes[quantityIndex] = qt;
    setProducts(productTemp);
    setProductValidation(productValidationTemp);
  };
  const handleAddMore = () => {
    let productTemp = [...products];
    let productValidationTemp = [...productValidation];
    let priceLoadingTemp = [...priceLoading];
    const allObjectsHaveQuantityTypeWithStock = productTemp.every((obj: any) =>
      obj.quantityTypes.some(
        (quantityType: any) =>
          quantityType.stockCount && quantityType.stockCount > 0,
      ),
    );
    if (allObjectsHaveQuantityTypeWithStock) {
      productTemp.push({
        variant: "",
        quantityTypes: [],
        selectedOption: null,
      });
      productValidationTemp.push({
        variant: false,
        quantityTypes: false,
      });
      priceLoadingTemp.push(false);
    } else {
      productTemp.forEach((product: any, index: number) => {
        const hasStockCountGreaterThanZero = product.quantityTypes.some(
          (quantityType: any) =>
            quantityType.stockCount !== undefined &&
            quantityType.stockCount > 0,
        );
        if (!hasStockCountGreaterThanZero) {
          productValidationTemp[index].quantityTypes = true;
        }
      });
    }
    setProductValidation(productValidationTemp);
    setProducts(productTemp);
    setPriceLoading(priceLoadingTemp);
  };
  const handleRemove = (indexToRemove: number) => {
    let productTemp = [...products];
    let productValidationTemp = [...productValidation];
    let priceLoadingTemp = [...priceLoading];
    productValidationTemp.splice(indexToRemove, 1);
    productTemp.splice(indexToRemove, 1);
    priceLoadingTemp.splice(indexToRemove, 1);
    setProducts(productTemp);
    setProductValidation(productValidationTemp);
    setPriceLoading(priceLoadingTemp);
  };

  const { currentUser } = useAuth();
  // same rule as the server: the Admin account, else "Edit Draft PO" (never by role name)
  const canSaveDraft =
    currentUser?.userType === Admin ||
    Method.hasPermission(PermissionModule.POManagement, Permissions.Draft_PO_Edit, currentUser);
  const [savingDraft, setSavingDraft] = useState(false);

  /** Saves the products to the supplier's draft PO (Draft PO tab) instead of a Pending PO. */
  const handleSaveDraft = async () => {
    if (!selectedSeller) {
      error("Vendor is required");
      return;
    }
    if (!selectedWarehouse?._id) {
      error("Warehouse is required");
      return;
    }
    const lines: DraftLineInput[] = [];
    const multi: string[] = [];
    products
      .filter((p) => p.variant)
      .forEach((p) => {
        const formTypes: PoFormQtyType[] = p.quantityTypes || [];
        const withQty = formTypes.filter((q) => Number(q.stockCount) > 0);
        if (!withQty.length) return;
        // the product name is on the selected option (form rows only hold the id)
        const name: string = p.selectedOption?.title || p.variant;
        if (withQty.length > 1) multi.push(name);
        const q = withQty[0];
        lines.push({
          variant: p.variant,
          quantityType: Number(q.type),
          quantity: Number(q.stockCount),
        });
      });
    if (!lines.length) {
      error("Enter a quantity for at least one product");
      return;
    }
    if (multi.length) {
      error(`A draft holds one qty type per product. Keep one qty type for: ${multi.slice(0, 3).join(", ")}`);
      return;
    }
    setSavingDraft(true);
    const api = new APICallService(
      PURCHASEORDER.createDraftPurchaseOrder,
      { warehouse: selectedWarehouse._id, business: selectedSeller, lines },
      "",
      "",
      "",
      "",
      PermissionModule.POManagement,
      selectedWarehouse._id
    );
    const res = await api.callAPI();
    setSavingDraft(false);
    if (res && res.refKey) {
      // several drafts when the supplier's products are in different groups (Vendor Profile > Groups)
      success(Array.isArray(res.savedTo) && res.savedTo.length > 1 ? `Saved to drafts ${res.savedTo.join(", ")} (one per supplier group)` : `Saved to draft ${res.refKey}`);
      setKey(listPurchaseOrder.currentTab, DRAFT_PO_TAB);
      setKey(listPurchaseOrder.page, 1);
      navigate("/UProcure/purchase-order-management", { state: { tab: DRAFT_PO_TAB } });
    }
  };

  const handleSubmit = async () => {
    if (!selectedSeller) {
      error("Vendor is required");
      return;
    }
    if (!selectedWarehouse) {
      error("Warehouse is required");
      return;
    }
    if (!deliveryDate) {
      error("Delivery date is required");
      return;
    }
    try {
      setLoading(true);
      let productTemp = [...products];
      let productValidationTemp = [...productValidation];

      // Build variants payload from `products` state
      // Only include quantity types with stockCount > 0 and purchaseAmount > 0
      const variants = products
        .filter((p: any) => p.variant)
        .map((p: any) => ({
          variant: p.variant,
          quantityTypes: (p.quantityTypes || [])
            .filter((q: any) => {
              const stockCount = Number(q.stockCount) || 0;
              const purchaseAmount = Number(q.purchaseAmount) || 0;
              return stockCount > 0 && purchaseAmount > 0;
            })
            .map((q: any) => ({
              type: q.type != null ? q.type.toString() : q.type,
              stockCount: Number(q.stockCount) || 0,
              amount: Number(q.amount) || 0,
              discountAmt: q.discountAmt || 0,
              discountAmtEnabled: q.discountAmtEnabled || false,
              discountByQuantitiesEnabled:
                q.discountByQuantitiesEnabled || false,
              discountsByQuantities: q.discountsByQuantities || [],
              purchaseAmount: Number(q.purchaseAmount) || 0,
              purchaseDiscountAmt: q.purchaseDiscountAmt || 0,
            })),
        }));

      if (productTemp.length) {
        productTemp.forEach((product: any, index: number) => {
          // Check if product has at least one valid quantity type (stockCount > 0 AND purchaseAmount > 0)
          const hasValidQuantityType = product.quantityTypes.some(
            (quantityType: any) => {
              const stockCount = Number(quantityType.stockCount) || 0;
              const purchaseAmount = Number(quantityType.purchaseAmount) || 0;
              return stockCount > 0 && purchaseAmount > 0;
            },
          );
          if (!hasValidQuantityType) {
            productValidationTemp[index].variant = true;
            productValidationTemp[index].quantityTypes = true;
          }
        });
        setProductValidation(productValidationTemp);
        setProducts(productTemp);
        // Check if any validation errors exist
        const hasErrors = productValidationTemp.some(
          (pv: any) => pv.variant || pv.quantityTypes,
        );
        if (hasErrors) {
          setLoading(false);
          error(
            "Please ensure all products have at least one valid quantity type with both quantity and price",
          );
          return;
        }
      } else {
        setLoading(false);
        error("Please add at least one product");
        return;
      }

      const params = {
        shipment: {
          type: shipmentType,
          date: deliveryDate
            ? new Date(deliveryDate.setHours(12, 0, 0, 0)).toISOString()
            : null,
        },
        type: 1,
        shipmentFor: 1,
        businessActivationDate:
          sellerData.find((s: any) => s._id === selectedSeller)?.KYC
            ?.profileUpdatedAt || null,
        businessId: selectedSeller,
        warehouseId: selectedWarehouse?._id,
        variants,
      };

      const apiService = new APICallService(
        PURCHASEORDER.addPurchaseOrder,
        params,
        {
          id: selectedSeller,
        },
        "",
        "",
        "",
        WarehouseManger,
        selectedWarehouse?._id || "",
      );
      const response = await apiService.callAPI();
      if (response) {
        // New POs are status=submitted (Pending). Clear sticky list filters and open Pending
        removeKey(listPurchaseOrder.search);
        removeKey(listPurchaseOrder.filterStartDate);
        removeKey(listPurchaseOrder.filterEndDate);
        removeKey(listPurchaseOrder.businessId);
        removeKey(listPurchaseOrder.category);
        removeKey(listPurchaseOrder.subCategory);
        removeKey(listPurchaseOrder.groupCategory);
        setKey(listPurchaseOrder.currentTab, 1);
        setKey(listPurchaseOrder.page, 1);
        navigate("/UProcure/purchase-order-management", { state: { tab: 1, refresh: Date.now() } });
      }
    } catch (error) {
      console.error("Error creating purchase order:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {fetchLoader ? (
        <div className="d-flex align-items-center justify-content-center">
          <Loader loading={fetchLoader} />
        </div>
      ) : (
        <div className="p-5">
          <div className="mb-5">
            <h1 className="fs-22 fw-bolder">Add Purchase Order</h1>
          </div>
          <Card className="border bg-f9f9f9 mb-5">
            <Card.Body>
              <Row className="align-items-center">
                <Col md={6} lg={6} className="mb-5">
                  <Form.Label className="fs-16 fw-500">Supplier</Form.Label>
                  <CustomSelect
                    backgroundColor="#ffff"
                    isLoading={fetchLoader}
                    options={
                      sellerData && sellerData.length
                        ? sellerData.map((val: any) => {
                            return {
                              label: (
                                <>
                                  <div className="symbol symbol-30px border me-3">
                                    <img
                                      src={val.image}
                                      className="object-fit-contain"
                                      alt=""
                                    />
                                  </div>
                                  <span className="fs-16 fw-600 text-black mb-0">
                                    {val.ownerName}
                                  </span>
                                </>
                              ),
                              value: val._id,
                              creationDate: val._createdAt,
                              // Include search term so async API results pass CustomSelect's client filter
                              title: `${val.name || ""} ${val.ownerName || ""} ${vendorSearchTerm}`,
                            };
                          })
                        : []
                    }
                    isDisabled={loading}
                    onMenuScrollToBottom={onMenuScrollToBottom}
                    onMenuClose={() => {
                      onMenuClose();
                    }}
                    onMenuOpen={() => {
                      onMenuOpen();
                    }}
                    onInputChange={(newValue: any, { action }: any) => {
                      if (action === "input-change") {
                        handleInputChange(newValue);
                      }
                    }}
                    onChange={(event: any) => {
                      handleSeller(event);
                    }}
                  />
                </Col>
                <Col md={6} lg={6} className="mb-5">
                  <Form.Label className="fs-16 fw-500 text-dark pb-2">
                    Requested For
                  </Form.Label>
                  <div className="d-flex flex-wrap gap-2">
                    <div className="form-check form-check-custom form-check-solid me-6 mb-lg-0">
                      <input
                        className="form-check-input h-30px w-30px h-md-35px w-md-35px"
                        type="checkbox"
                        checked={umartData}
                        onChange={() => {
                          setUmartData(true);
                        }}
                        disabled={loading}
                      />
                      <label className="form-check-label text-dark fs-16 fw-600 ms-3">
                        U-Trade
                      </label>
                    </div>
                    <div className="form-check form-check-custom form-check-solid me-6 mb-lg-0">
                      <input
                        className="form-check-input h-30px w-30px h-md-35px w-md-35px"
                        type="checkbox"
                        checked={!umartData}
                        onChange={() => {
                          setUmartData(false);
                        }}
                        disabled={loading}
                      />
                      <label className="form-check-label text-dark fs-16 fw-600 ms-3">
                        U-Mart
                      </label>
                    </div>
                  </div>
                </Col>
                <Col md={6} lg={6} className="mb-5">
                  <Form.Label className="fs-16 fw-500">
                    Select Warehouse
                  </Form.Label>
                  <CustomSelect
                    backgroundColor="#ffff"
                    isLoading={fetchLoader}
                    value={
                      selectedWarehouse
                        ? {
                            label: (
                              <span className="fs-16 fw-600 text-black mb-0">
                                {selectedWarehouse.title}
                              </span>
                            ),
                            value: selectedWarehouse._id,
                          }
                        : null
                    }
                    options={
                      warehouseList && warehouseList.length
                        ? warehouseList.map((val) => {
                            return {
                              label: (
                                <span className="fs-16 fw-600 text-black mb-0">
                                  {val.title}
                                </span>
                              ),
                              value: val._id,
                              title: val.title,
                            };
                          })
                        : []
                    }
                    isDisabled={loading}
                    onMenuClose={() => {
                      onMenuClose();
                    }}
                    onMenuOpen={() => {
                      onMenuOpen();
                    }}
                    onChange={(event: any) => {
                      const matched = warehouseList.find(
                        (w) => w._id === event.value,
                      );
                      setSelectedWarehouse(matched || null);
                    }}
                  />
                </Col>
                <Col md={6} lg={6} className="mb-5">
                  <Form.Label className="fs-16 fw-500 text-dark pb-2">
                    Transportation service provided by
                  </Form.Label>
                  <div className="d-flex flex-wrap gap-2">
                    <div className="form-check form-check-custom form-check-solid me-6 mb-lg-0">
                      <input
                        className="form-check-input h-30px w-30px h-md-35px w-md-35px"
                        type="radio"
                        checked={shipmentType === UTradeTransport}
                        onChange={() => setShipmentType(UTradeTransport)}
                        disabled={loading}
                      />
                      <label className="form-check-label text-dark fs-16 fw-600 ms-3">
                        U Trade transport service
                      </label>
                    </div>
                    <div className="form-check form-check-custom form-check-solid me-6 mb-lg-0">
                      <input
                        className="form-check-input h-30px w-30px h-md-35px w-md-35px"
                        type="radio"
                        checked={shipmentType === SelfTransport}
                        onChange={() => setShipmentType(SelfTransport)}
                        disabled={loading}
                      />
                      <label className="form-check-label text-dark fs-16 fw-600 ms-3">
                        Supplier Transport
                      </label>
                    </div>
                  </div>
                </Col>
                <Col md={6} lg={6} className="mb-5">
                  <Form.Label className="fs-16 fw-500 text-dark">
                    Delivery date
                  </Form.Label>
                  <CustomDatePicker
                    className="form-control bg-white min-h-30px fs-16 fw-bold text-dark min-w-md-288px min-w-175px"
                    onChange={handleDateChange}
                    selected={deliveryDate}
                    dateFormat="dd/MM/yyyy"
                    placeholderText="Select delivery date"
                    isClearable={true}
                    inputTextBG="bg-white"
                    disabled={!selectedSeller}
                    filterDate={isDateDisabled}
                  />
                </Col>
              </Row>
            </Card.Body>
          </Card>
          {!selectedSeller && !productFetchLoader ? (
            <Card className="border bg-f9f9f9 mb-5">
              <Card.Header className="border-bottom-0">
                <Card.Title className="fs-22 fw-bolder">
                  Products & Quantities
                </Card.Title>
              </Card.Header>
              <Card.Body>
                <div className="fs-18 fw-500 text-center min-h-50px">
                  Please select a supplier to see products.
                </div>
              </Card.Body>
            </Card>
          ) : (
            <>
              <Card className="border bg-f9f9f9 py-3">
                <Card.Header className="border-bottom-0 mb-3">
                  <Card.Title className="fs-22 fw-bolder">
                    Products & Quantities
                  </Card.Title>
                </Card.Header>
                <Card.Body className="pt-0 pb-5">
                  <>
                    {productFetchLoader ? (
                      <>
                        <div className="d-flex align-items-center justify-content-center min-h-100px">
                          <Loader loading={productFetchLoader} />
                        </div>
                      </>
                    ) : (
                      <>
                        {products.length ? (
                          <>
                            {products.map((val: any, index: number) => {
                              const isPriceLoading = priceLoading[index];
                              return (
                                <Fragment key={val.variant || index}>
                                  <Row className="align-items-center g-3 g-lg-5">
                                    <Col md={5} lg={4}>
                                      <CustomSelect
                                        backgroundColor="#ffff"
                                        isLoading={productFetchLoader}
                                        options={
                                          sellerProducts &&
                                          sellerProducts.length
                                            ? sellerProducts
                                                .filter(
                                                  (val: any) =>
                                                    !products.some(
                                                      (item: any) =>
                                                        item.variant ===
                                                        val._id,
                                                    ),
                                                )
                                                .map((val: any) => {
                                                  return {
                                                    label: (
                                                      <>
                                                        <div className="symbol symbol-30px border me-3">
                                                          <img
                                                            src={
                                                              val.media[0].url
                                                            }
                                                            className="object-fit-contain"
                                                            alt=""
                                                          />
                                                        </div>
                                                        <span className="fs-16 fw-600 text-black mb-0">
                                                          {val.title.replace(
                                                            /\s*\)\s*/g,
                                                            ")",
                                                          )}
                                                        </span>
                                                      </>
                                                    ),
                                                    value: val._id,
                                                    title: val.title,
                                                  };
                                                })
                                            : []
                                        }
                                        value={
                                          sellerProducts &&
                                          sellerProducts.length
                                            ? sellerProducts
                                                .filter(
                                                  (item: any) =>
                                                    item._id === val.variant,
                                                )
                                                .map((val: any) => ({
                                                  label: (
                                                    <>
                                                      <div className="symbol symbol-30px border me-3">
                                                        <img
                                                          src={val.media[0].url}
                                                          className="object-fit-contain"
                                                          alt=""
                                                        />
                                                      </div>
                                                      <span className="fs-16 fw-600 text-black mb-0">
                                                        {val.title.replace(
                                                          /\s*\)\s*/g,
                                                          ")",
                                                        )}
                                                      </span>
                                                    </>
                                                  ),
                                                  value: val._id,
                                                  title: val.title,
                                                }))
                                            : null // Set to null when no match is found
                                        }
                                        onMenuScrollToBottom={
                                          onMenuProductScrollToBottom
                                        }
                                        onMenuClose={() => {
                                          onMenuProductClose();
                                        }}
                                        onMenuOpen={() => {
                                          onMenuProductOpen();
                                        }}
                                        onInputChange={(
                                          newValue: any,
                                          { action }: any,
                                        ) => {
                                          if (action === "input-change") {
                                            handleProductInputChange(newValue);
                                          }
                                        }}
                                        onChange={(event: any) => {
                                          handleProductSelect(event, index);
                                        }}
                                        placeholder="Select product"
                                        // isDisabled={loading}
                                      />
                                      {productValidation[index]
                                        .quantityTypes ? (
                                        <Form.Label className="fs-16 fw-500"></Form.Label>
                                      ) : (
                                        <></>
                                      )}
                                    </Col>
                                    {val.quantityTypes ? (
                                      //  &&val.quantityTypes.length
                                      <>
                                        <Col
                                          xs={11}
                                          md={6}
                                          lg={8}
                                          style={{ position: "relative" }}
                                        >
                                          <Form.Label className="fs-16 fw-500">
                                            Quantity
                                          </Form.Label>
                                          <Row className="align-items-start g-2 g-md-3">
                                            {/* Cartons input */}
                                            <Col xs={12} sm={6} md={4} lg={4}>
                                              {(() => {
                                                const arr =
                                                  val.quantityTypes || [];
                                                const qIdx = arr.findIndex(
                                                  (item: any) =>
                                                    item.type ===
                                                      CartonWithDozens ||
                                                    item.type ===
                                                      CartonWithPieces,
                                                );
                                                const qt = arr[qIdx];
                                                // Enable if type exists in quantityTypes
                                                const isCartonEnabled =
                                                  qIdx !== -1;
                                                return (
                                                  <>
                                                    <InputGroup
                                                      className="border border-r8px overflow-hidden p-0"
                                                      style={{ minWidth: 0 }}
                                                    >
                                                      <Form.Control
                                                        className="form-control-custom fs-16 fw-600 border-0 text-end bg-white padding-05"
                                                        placeholder="e.g. 10"
                                                        value={
                                                          qt?.stockCount ?? ""
                                                        }
                                                        onWheel={(e) => {
                                                          (
                                                            e.target as HTMLInputElement
                                                          ).blur();
                                                        }}
                                                        onChange={(
                                                          event: any,
                                                        ) => {
                                                          const v = (
                                                            (event &&
                                                              event.target &&
                                                              event.target
                                                                .value) ||
                                                            ""
                                                          )
                                                            .toString()
                                                            .trimStart();
                                                          handleQuantityChange(
                                                            v,
                                                            index,
                                                            qIdx,
                                                            qt?.type ??
                                                              CartonWithPieces,
                                                          );
                                                        }}
                                                        type="number"
                                                        disabled={
                                                          !isCartonEnabled ||
                                                          isPriceLoading
                                                        }
                                                      />
                                                      <InputGroup.Text
                                                        className={clsx(
                                                          "fs-16 fw-600 border-0 px-3",
                                                          isCartonEnabled
                                                            ? "text-dark"
                                                            : "text-gray",
                                                        )}
                                                      >
                                                        {isPriceLoading ? (
                                                          <span className="spinner-border spinner-border-sm" />
                                                        ) : (
                                                          "cartons"
                                                        )}
                                                      </InputGroup.Text>
                                                    </InputGroup>

                                                    <div className="mt-2">
                                                      <InputGroup
                                                        className="border border-r8px overflow-hidden p-0"
                                                        style={{ minWidth: 0 }}
                                                      >
                                                        <Form.Control
                                                          className="form-control-custom fs-14 fw-600 border-0 text-end bg-white padding-05"
                                                          placeholder="Price Incl. of tax"
                                                          value={
                                                            qt?.purchaseAmount ??
                                                            ""
                                                          }
                                                          readOnly
                                                          type="number"
                                                          disabled={
                                                            !isCartonEnabled ||
                                                            isPriceLoading
                                                          }
                                                        />
                                                        <InputGroup.Text
                                                          className={clsx(
                                                            "fs-16 fw-600 border-0 px-3",
                                                            isCartonEnabled
                                                              ? "text-dark"
                                                              : "text-gray",
                                                          )}
                                                        >
                                                          {isPriceLoading ? (
                                                            <span className="spinner-border spinner-border-sm" />
                                                          ) : (
                                                            "price"
                                                          )}
                                                        </InputGroup.Text>
                                                      </InputGroup>
                                                      {isCartonEnabled &&
                                                        !isPriceLoading &&
                                                        !(
                                                          qt?.purchaseAmount > 0
                                                        ) && (
                                                          <div className="fs-12 fw-600 text-danger mt-2">
                                                            Contact admin to set
                                                            the cost price
                                                          </div>
                                                        )}
                                                      <LastPoQty info={lastPo[val?.variant]?.[Number(qt?.type)]} />
                                                      {val?.forecastedDaysData?.quantityTypes?.map(
                                                        (
                                                          qt: any,
                                                          qIndex: number,
                                                        ) =>
                                                          qt.forecastedDays !==
                                                            null &&
                                                          (qt.type ===
                                                            CartonWithDozens ||
                                                            qt.type ===
                                                              CartonWithPieces) && (
                                                            <div
                                                              key={qIndex}
                                                              className="fs-12 fw-600 text-warning mt-2"
                                                            >
                                                              Inventory
                                                              stock-out in{" "}
                                                              {
                                                                qt.forecastedDays
                                                              }{" "}
                                                              days for{" "}
                                                              {getQuantityTypeLabel(
                                                                qt.type,
                                                              )}
                                                              .
                                                            </div>
                                                          ),
                                                      )}
                                                    </div>
                                                  </>
                                                );
                                              })()}
                                            </Col>

                                            {/* Dozens input */}
                                            <Col xs={12} sm={6} md={4} lg={4}>
                                              {(() => {
                                                const arr =
                                                  val.quantityTypes || [];
                                                const qIdx = arr.findIndex(
                                                  (item: any) =>
                                                    item.type === Dozen,
                                                );
                                                const qt = arr[qIdx];
                                                const isDozenEnabled =
                                                  qIdx !== -1;
                                                return (
                                                  <>
                                                    <InputGroup
                                                      className="border border-r8px overflow-hidden p-0"
                                                      style={{ minWidth: 0 }}
                                                    >
                                                      <Form.Control
                                                        className="form-control-custom fs-16 fw-600 border-0 text-end bg-white padding-05"
                                                        placeholder="e.g. 1"
                                                        value={
                                                          qt?.stockCount ?? ""
                                                        }
                                                        onWheel={(e) => {
                                                          (
                                                            e.target as HTMLInputElement
                                                          ).blur();
                                                        }}
                                                        onChange={(
                                                          event: any,
                                                        ) => {
                                                          const v = (
                                                            (event &&
                                                              event.target &&
                                                              event.target
                                                                .value) ||
                                                            ""
                                                          )
                                                            .toString()
                                                            .trimStart();
                                                          handleQuantityChange(
                                                            v,
                                                            index,
                                                            qIdx,
                                                            Dozen,
                                                          );
                                                        }}
                                                        type="number"
                                                        disabled={
                                                          !isDozenEnabled ||
                                                          isPriceLoading
                                                        }
                                                      />
                                                      <InputGroup.Text
                                                        className={clsx(
                                                          "fs-16 fw-600 border-0 px-3",
                                                          isDozenEnabled
                                                            ? "text-dark"
                                                            : "text-gray",
                                                        )}
                                                      >
                                                        {isPriceLoading ? (
                                                          <span className="spinner-border spinner-border-sm" />
                                                        ) : (
                                                          "dozens"
                                                        )}
                                                      </InputGroup.Text>
                                                    </InputGroup>

                                                    <div className="mt-2">
                                                      <InputGroup
                                                        className="border border-r8px overflow-hidden p-0"
                                                        style={{ minWidth: 0 }}
                                                      >
                                                        <Form.Control
                                                          className="form-control-custom fs-14 fw-600 border-0 text-end bg-white padding-05"
                                                          placeholder="Price Incl. of tax"
                                                          value={
                                                            qt?.purchaseAmount ??
                                                            ""
                                                          }
                                                          readOnly
                                                          type="number"
                                                          disabled={
                                                            !isDozenEnabled ||
                                                            isPriceLoading
                                                          }
                                                        />
                                                        <InputGroup.Text
                                                          className={clsx(
                                                            "fs-16 fw-600 border-0 px-3",
                                                            isDozenEnabled
                                                              ? "text-dark"
                                                              : "text-gray",
                                                          )}
                                                        >
                                                          {isPriceLoading ? (
                                                            <span className="spinner-border spinner-border-sm" />
                                                          ) : (
                                                            "price"
                                                          )}
                                                        </InputGroup.Text>
                                                      </InputGroup>
                                                      {isDozenEnabled &&
                                                        !isPriceLoading &&
                                                        !(
                                                          qt?.purchaseAmount > 0
                                                        ) && (
                                                          <div className="fs-12 fw-600 text-danger mt-2">
                                                            Contact admin to set
                                                            the cost price
                                                          </div>
                                                        )}
                                                      <LastPoQty info={lastPo[val?.variant]?.[Number(qt?.type)]} />
                                                      {val?.forecastedDaysData?.quantityTypes?.map(
                                                        (
                                                          qt: any,
                                                          qIndex: number,
                                                        ) =>
                                                          qt.forecastedDays !==
                                                            null &&
                                                          qt.type === Dozen && (
                                                            <div
                                                              key={qIndex}
                                                              className="fs-12 fw-600 text-warning mt-2"
                                                            >
                                                              Inventory
                                                              stock-out in{" "}
                                                              {
                                                                qt.forecastedDays
                                                              }{" "}
                                                              days for{" "}
                                                              {qt.type === Dozen
                                                                ? "dozens"
                                                                : ""}
                                                              .
                                                            </div>
                                                          ),
                                                      )}
                                                    </div>
                                                  </>
                                                );
                                              })()}
                                            </Col>

                                            {/* Pieces input */}
                                            <Col xs={12} sm={6} md={4} lg={4}>
                                              {(() => {
                                                const arr =
                                                  val.quantityTypes || [];
                                                const qIdx = arr.findIndex(
                                                  (item: any) =>
                                                    item.type === Piece,
                                                );
                                                const qt = arr[qIdx];
                                                const isPieceEnabled =
                                                  qIdx !== -1;
                                                return (
                                                  <>
                                                    <InputGroup
                                                      className="border border-r8px overflow-hidden p-0"
                                                      style={{ minWidth: 0 }}
                                                    >
                                                      <Form.Control
                                                        className="form-control-custom fs-16 fw-600 border-0 text-end bg-white padding-05"
                                                        placeholder="e.g. 1000"
                                                        value={
                                                          qt?.stockCount ?? ""
                                                        }
                                                        onWheel={(e) => {
                                                          (
                                                            e.target as HTMLInputElement
                                                          ).blur();
                                                        }}
                                                        onChange={(
                                                          event: any,
                                                        ) => {
                                                          const v = (
                                                            (event &&
                                                              event.target &&
                                                              event.target
                                                                .value) ||
                                                            ""
                                                          )
                                                            .toString()
                                                            .trimStart();
                                                          handleQuantityChange(
                                                            v,
                                                            index,
                                                            qIdx,
                                                            Piece,
                                                          );
                                                        }}
                                                        type="number"
                                                        disabled={
                                                          !isPieceEnabled ||
                                                          isPriceLoading
                                                        }
                                                      />
                                                      <InputGroup.Text
                                                        className={clsx(
                                                          "fs-16 fw-600 border-0 px-3",
                                                          isPieceEnabled
                                                            ? "text-dark"
                                                            : "text-gray",
                                                        )}
                                                      >
                                                        {isPriceLoading ? (
                                                          <span className="spinner-border spinner-border-sm" />
                                                        ) : (
                                                          "pieces"
                                                        )}
                                                      </InputGroup.Text>
                                                    </InputGroup>

                                                    <div className="mt-2">
                                                      <InputGroup
                                                        className="border border-r8px overflow-hidden p-0"
                                                        style={{ minWidth: 0 }}
                                                      >
                                                        <Form.Control
                                                          className="form-control-custom fs-14 fw-600 border-0 text-end bg-white padding-05"
                                                          placeholder="Price Incl. of tax"
                                                          value={
                                                            qt?.purchaseAmount ??
                                                            ""
                                                          }
                                                          readOnly
                                                          type="number"
                                                          disabled={
                                                            !isPieceEnabled ||
                                                            isPriceLoading
                                                          }
                                                        />
                                                        <InputGroup.Text
                                                          className={clsx(
                                                            "fs-16 fw-600 border-0 px-3",
                                                            isPieceEnabled
                                                              ? "text-dark"
                                                              : "text-gray",
                                                          )}
                                                        >
                                                          {isPriceLoading ? (
                                                            <span className="spinner-border spinner-border-sm" />
                                                          ) : (
                                                            "price"
                                                          )}
                                                        </InputGroup.Text>
                                                      </InputGroup>
                                                      {isPieceEnabled &&
                                                        !isPriceLoading &&
                                                        !(
                                                          qt?.purchaseAmount > 0
                                                        ) && (
                                                          <div className="fs-12 fw-600 text-danger mt-2">
                                                            Contact admin to set
                                                            the cost price
                                                          </div>
                                                        )}
                                                      <LastPoQty info={lastPo[val?.variant]?.[Number(qt?.type)]} />
                                                      {val?.forecastedDaysData?.quantityTypes?.map(
                                                        (
                                                          qt: any,
                                                          qIndex: number,
                                                        ) =>
                                                          qt.forecastedDays !==
                                                            null &&
                                                          qt.type === Piece && (
                                                            <div
                                                              key={qIndex}
                                                              className="fs-12 fw-600 text-warning mt-2"
                                                            >
                                                              Inventory
                                                              stock-out in{" "}
                                                              {
                                                                qt.forecastedDays
                                                              }{" "}
                                                              days for{" "}
                                                              {qt.type === Piece
                                                                ? "pieces"
                                                                : ""}
                                                              .
                                                            </div>
                                                          ),
                                                      )}
                                                    </div>
                                                  </>
                                                );
                                              })()}
                                            </Col>

                                            {/* keep space for additional responsive column if needed */}
                                            <Col
                                              xs={12}
                                              sm={6}
                                              md={12}
                                              lg={3}
                                            ></Col>
                                          </Row>
                                          {productValidation[index]
                                            .quantityTypes ? (
                                            <Form.Label className="fs-12 fw-500 mt-2 text-danger">
                                              At least one stock must have a
                                              value greater than 0.
                                            </Form.Label>
                                          ) : (
                                            <></>
                                          )}
                                          {productValidation[index].variant ? (
                                            <Form.Label className="fs-12 fw-500 mt-2 text-danger">
                                              Price must have a value greater
                                              than 0.
                                            </Form.Label>
                                          ) : (
                                            <></>
                                          )}
                                          {products.length > 1 && (
                                            <Button
                                              variant="link"
                                              className="btn-flush"
                                              onClick={() => {
                                                handleRemove(index);
                                              }}
                                              disabled={loading}
                                              style={{
                                                position: "absolute",
                                                right: 0,
                                                top: 0,
                                              }}
                                            >
                                              <img
                                                width={15}
                                                height={15}
                                                className="img-fluid"
                                                src={GrayCross}
                                                alt=""
                                              />
                                            </Button>
                                          )}
                                        </Col>
                                      </>
                                    ) : (
                                      <Col xs={11} md={6} lg={8}></Col>
                                    )}
                                  </Row>
                                  {index === products.length - 1 ? (
                                    <>
                                      <>
                                        {sellerProducts &&
                                        sellerProducts.length ? (
                                          sellerProducts.filter(
                                            (val: any) =>
                                              !products.some(
                                                (item: any) =>
                                                  item.variant === val._id,
                                              ),
                                          ).length ? (
                                            <Row className="d-flex justify-content-start">
                                              <Col className="d-flex justify-content-start mt-13">
                                                <Button
                                                  variant="link"
                                                  className="btn-flush "
                                                  disabled={loading}
                                                  onClick={() => {
                                                    handleAddMore();
                                                  }}
                                                >
                                                  <span className="text-primary fs-16 fw-600">
                                                    + Add new goods
                                                  </span>
                                                </Button>
                                              </Col>
                                            </Row>
                                          ) : (
                                            <></>
                                          )
                                        ) : (
                                          <></>
                                        )}
                                      </>
                                    </>
                                  ) : (
                                    <>
                                      <div className="separator my-6"></div>
                                    </>
                                  )}
                                </Fragment>
                              );
                            })}
                          </>
                        ) : (
                          <>
                            <div className="fs-18 fw-500 text-center min-h-50px">
                              No products available
                            </div>
                          </>
                        )}
                      </>
                    )}
                  </>
                </Card.Body>
              </Card>
            </>
          )}
          <div className="d-flex justify-content-end gap-3 mt-6">
            {canSaveDraft && (
              <Button
                size="lg"
                variant="light-primary"
                className="min-h-lg-60px"
                onClick={() => handleSaveDraft()}
                disabled={loading || savingDraft}
                title="Keep as a draft on the Draft PO tab; it is not sent for approval"
              >
                <span className="indicator-label fs-16 fw-bold">
                  {savingDraft ? "Saving..." : "Save as Draft"}
                </span>
              </Button>
            )}
            <Button
              size="lg"
              className="uw-btn uw-btn-primary uw-btn-lg min-h-lg-60px"
              onClick={() => handleSubmit()}
              disabled={loading}
            >
              {!loading && (
                <span className="indicator-label fs-16 fw-bold">
                  Confirm request
                </span>
              )}
              {loading && (
                <span
                  className="indicator-progress fs-16 fw-bold"
                  style={{ display: "block" }}
                >
                  Please wait...
                  <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                </span>
              )}
            </Button>
          </div>
        </div>
      )}
    </>
  );
};
export default AddPurchaseOrder;
